/**
 * MODULE: EmailTemplateCreateModal
 * PURPOSE: Rich email template creation with variables, subject, body, sharing
 * DEPENDENCIES: flowbite-react, @domain/templates/frontend (createEmailTemplate golden path)
 * AGENT_SAFETY: 🟢 SAFE
 * Recent: 2026-06-19 [2.2.1455] — Modal footer stays inside white bounds (form flex column; body scrolls; footer shrink-0).
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  TextInput,
  Textarea,
  Select,
} from 'flowbite-react';
import {
  SparklesIcon,
  UserIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';
import {
  createEmailTemplate,
  extractEmailTemplateSource,
  templatesApi,
} from '@domain/templates/frontend';
import { useToast } from '@design-system/components';
import { CREATE_CATEGORY_OPTIONS } from '../../config/template-categories';
import InsertFieldDropdown from './InsertFieldDropdown';
import QuickInsertFields from './QuickInsertFields';
import type { EmailTemplateCreatePrefill } from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import { ContactFieldsService, type ContactField } from '../../services/contactFieldsService';

interface EmailTemplateCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result?: { title?: string }) => void;
  /** Optional category to preselect when opening (e.g. 'welcome', 'follow-up', 'outreach', 'newsletter') */
  initialCategory?: string;
  /** Optional values from assistant form_catalog or context chips. */
  initialPrefill?: EmailTemplateCreatePrefill;
}

export default function EmailTemplateCreateModal({
  isOpen,
  onClose,
  onSuccess,
  initialCategory,
  initialPrefill,
}: EmailTemplateCreateModalProps) {
  const categories = CREATE_CATEGORY_OPTIONS.email;
  const defaultCategory = initialCategory && categories.some((c) => c.value === initialCategory)
    ? initialCategory
    : (categories[0]?.value ?? 'outreach');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: defaultCategory,
    subject: '',
    previewText: '',
    bodyContent: '',
    sharedWith: 'everyone' as 'everyone' | 'team' | 'private',
  });

  const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
  const subjectInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const prefillCategory =
      initialPrefill?.category &&
      categories.some((c) => c.value === initialPrefill.category)
        ? initialPrefill.category
        : defaultCategory;
    setFormData({
      name: initialPrefill?.name?.trim() ?? '',
      description: initialPrefill?.description?.trim() ?? '',
      category: prefillCategory,
      subject: initialPrefill?.subject?.trim() ?? '',
      previewText: '',
      bodyContent: initialPrefill?.bodyContent?.trim() ?? '',
      sharedWith: 'everyone',
    });
  }, [isOpen, defaultCategory, initialPrefill, categories]);

  const [isCreating, setIsCreating] = useState(false);
  const { showToast } = useToast();

  const insertFieldIntoSubject = (field: ContactField) => {
    if (subjectInputRef.current) {
      const input = subjectInputRef.current;
      const start = input.selectionStart ?? 0;
      const end = input.selectionEnd ?? 0;
      const newValue =
        formData.subject.substring(0, start) + field.placeholder + formData.subject.substring(end);
      setFormData({ ...formData, subject: newValue });
      setTimeout(() => {
        input.focus();
        input.setSelectionRange(start + field.placeholder.length, start + field.placeholder.length);
      }, 0);
    }
  };

  const insertFieldIntoBody = (field: ContactField) => {
    if (bodyTextareaRef.current) {
      const textarea = bodyTextareaRef.current;
      const start = textarea.selectionStart ?? 0;
      const end = textarea.selectionEnd ?? 0;
      const newValue =
        formData.bodyContent.substring(0, start) + field.placeholder + formData.bodyContent.substring(end);
      setFormData({ ...formData, bodyContent: newValue });
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + field.placeholder.length, start + field.placeholder.length);
      }, 0);
    }
  };

  const handleGenerateWithAI = async () => {
    const name = formData.name.trim() || 'Outreach email';
    const hints = [
      formData.description.trim(),
      formData.subject.trim() ? `Subject hint: ${formData.subject.trim()}` : '',
      formData.bodyContent.trim() ? `Body outline:\n${formData.bodyContent.trim()}` : '',
    ]
      .filter(Boolean)
      .join('\n');
    const prompt = [
      `Create a professional B2B email template named «${name}».`,
      hints || 'Warm outreach to a sales prospect with clear call to action.',
    ].join('\n');

    try {
      setIsCreating(true);
      const res = await templatesApi.generateEmail({
        prompt,
        tone: 'professional',
        purpose:
          formData.category === 'follow-up'
            ? 'follow_up'
            : formData.category === 'welcome'
              ? 'introduction'
              : 'outreach',
      });
      const t = res.data;
      const { subject, body } = extractEmailTemplateSource(t);
      showToast({
        message: 'AI template generated and saved to your library',
        variant: 'success',
      });
      setFormData({
        name: t.title || name,
        description: formData.description,
        category: formData.category,
        subject,
        previewText: formData.previewText,
        bodyContent: body,
        sharedWith: formData.sharedWith,
      });
      onSuccess({ title: t.title || name });
      onClose();
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : 'AI generation failed',
        variant: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast({ message: 'Please enter a template name', variant: 'error' });
      return;
    }
    if (!formData.subject.trim() || !formData.bodyContent.trim()) {
      showToast({ message: 'Subject and email body are required', variant: 'error' });
      return;
    }
    try {
      setIsCreating(true);
      await createEmailTemplate({
        title: formData.name.trim(),
        description: formData.description.trim() || undefined,
        category: formData.category,
        subject: formData.subject,
        body: formData.bodyContent,
        preview_text: formData.previewText || undefined,
        shared_with: formData.sharedWith,
      });
      showToast({
        message: 'Email template saved — visible in Sales Hub Library and Assistant',
        variant: 'success',
      });
      setFormData({
        name: '',
        description: '',
        category: defaultCategory,
        subject: '',
        previewText: '',
        bodyContent: '',
        sharedWith: 'everyone',
      });
      onSuccess({ title: formData.name.trim() });
      onClose();
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : 'Failed to create template',
        variant: 'error',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} size="4xl">
      <ModalHeader>
        <div className="flex items-center gap-3">
          <span className="text-2xl">✉️</span>
          <span>Create New Email Template</span>
        </div>
      </ModalHeader>

      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <ModalBody
          className="min-h-0 flex-1 overflow-y-auto"
          data-testid="email-template-create-modal"
        >
          <div className="space-y-6">
            {/* Header: Name and Owner */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email-tpl-name">Template Name *</Label>
                <TextInput
                  id="email-tpl-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., First outreach"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Owner</Label>
                <div className="mt-1 flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700">
                  <UserIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-900 dark:text-white">Current User</span>
                </div>
              </div>
            </div>

            {/* Subject and Sharing */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email-tpl-subject">Subject Line</Label>
                <div className="mt-1 flex gap-2">
                  <TextInput
                    id="email-tpl-subject"
                    ref={subjectInputRef}
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="e.g., How are you, {FIRST_NAME}?"
                    className="flex-1"
                  />
                  <InsertFieldDropdown
                    onFieldSelect={insertFieldIntoSubject}
                    buttonLabel="Insert Field"
                    buttonSize="sm"
                    buttonColor="gray"
                  />
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Shared with</Label>
                  <Select
                    value={formData.sharedWith}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        sharedWith: e.target.value as 'everyone' | 'team' | 'private',
                      })
                    }
                    className="mt-1"
                  >
                    <option value="everyone">Everyone</option>
                    <option value="team">My Team</option>
                    <option value="private">Private</option>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label htmlFor="email-tpl-category">Category *</Label>
                  <Select
                    id="email-tpl-category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                    className="mt-1"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Email Body Editor */}
            <div className="rounded-lg border dark:border-gray-600">
              <div className="border-b bg-gray-50 px-4 py-2 dark:border-gray-600 dark:bg-gray-700">
                <Label className="text-sm font-medium">Email Body</Label>
              </div>
              <div className="p-4">
                <QuickInsertFields onFieldSelect={insertFieldIntoBody} />
                <Textarea
                  ref={bodyTextareaRef}
                  value={formData.bodyContent}
                  onChange={(e) => setFormData({ ...formData, bodyContent: e.target.value })}
                  placeholder={'Hi {FIRST_NAME},\n\nI see you\'re interested in...'}
                  rows={10}
                  className="w-full resize-none border-0 focus:ring-0"
                />
              </div>
              <div className="flex items-center justify-between border-t bg-gray-50 px-4 py-2 dark:border-gray-600 dark:bg-gray-700">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Bold"
                  >
                    <span className="text-sm font-bold">B</span>
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Italic"
                  >
                    <span className="text-sm italic">I</span>
                  </button>
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Underline"
                  >
                    <span className="text-sm underline">U</span>
                  </button>
                  <div className="mx-2 h-6 w-px bg-gray-300 dark:bg-gray-600" />
                  <InsertFieldDropdown
                    onFieldSelect={insertFieldIntoBody}
                    buttonLabel="Insert Field"
                    buttonSize="xs"
                    buttonColor="gray"
                  />
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-gray-200 dark:hover:bg-gray-600"
                    title="Insert link"
                  >
                    <LinkIcon className="h-4 w-4" />
                    Link
                  </button>
                </div>
                <Button
                  type="button"
                  color="purple"
                  size="xs"
                  onClick={handleGenerateWithAI}
                >
                  <SparklesIcon className="mr-1 h-4 w-4" />
                  Generate with AI
                </Button>
              </div>
            </div>

            {/* Preview Text */}
            <div>
              <Label htmlFor="email-tpl-preview">Preview Text (optional)</Label>
              <TextInput
                id="email-tpl-preview"
                value={formData.previewText}
                onChange={(e) => setFormData({ ...formData, previewText: e.target.value })}
                placeholder="Text that appears below the subject line..."
                className="mt-1"
              />
              <p className="mt-1 text-xs text-gray-500">This text appears in the inbox preview</p>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="email-tpl-desc">Description (optional)</Label>
              <Textarea
                id="email-tpl-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of when to use this template..."
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">💡 Tip:</span> Your signature will be included
                automatically when you use this template.
              </p>
            </div>
          </div>
        </ModalBody>

        <ModalFooter className="shrink-0 border-t border-gray-200 dark:border-gray-600">
          <div className="flex w-full items-center justify-between">
            <span className="text-xs text-gray-500">
              {formData.bodyContent.length} characters
            </span>
            <div className="flex gap-3">
              <Button color="gray" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button color="purple" type="submit" disabled={isCreating}>
                {isCreating ? 'Saving...' : 'Save Template'}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
